import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { AdminLayout, PageHeader } from '../components/Layout';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  Database, HardDrive, Lock, FileText, Users,
  Settings, Mail, KeyRound, Loader2, Shield,
  Download, Upload, AlertTriangle
} from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatBytes(bytes) {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

const COLL_ICONS = {
  users: Users, submissions: FileText, templates: FileText,
  settings: Settings, invites: Mail, password_resets: KeyRound,
};

export default function AdminDatabase() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showImportConfirm, setShowImportConfirm] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => { loadStats(); }, []);

  const loadStats = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/database-stats`);
      setStats(res.data);
    } catch (err) {
      toast.error('Failed to load database stats');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API_URL}/admin/database/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      link.setAttribute('download', `auditguard_backup_${timestamp}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Database exported successfully');
    } catch (err) {
      toast.error('Failed to export database');
    } finally {
      setExporting(false);
    }
  };

  const handleImportSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.json')) {
      toast.error('Please select a .json backup file');
      return;
    }
    setImportFile(file);
    setShowImportConfirm(true);
  };

  const handleImportConfirm = async () => {
    if (!importFile) return;
    setImporting(true);
    setShowImportConfirm(false);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const res = await axios.post(`${API_URL}/admin/database/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(res.data.message);
      loadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to import database');
    } finally {
      setImporting(false);
      setImportFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-[#FF6B35]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        <PageHeader
          title="Database & Encryption"
          description="Storage statistics, encryption status, and backup management."
        />

        {/* Export / Import Section */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-8" data-testid="db-backup-section">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#FF6B35]/10 rounded-lg flex items-center justify-center">
                <Database className="h-5 w-5 text-[#FF6B35]" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Backup & Restore</h2>
                <p className="text-sm text-slate-500">Export before updates, import after redeployment</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={handleExport}
                disabled={exporting}
                className="bg-[#004E98] hover:bg-[#003d7a] text-white gap-2"
                data-testid="db-export-btn"
              >
                {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                {exporting ? 'Exporting...' : 'Export Database'}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                className="hidden"
                onChange={handleImportSelect}
                data-testid="db-import-file-input"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={importing}
                variant="outline"
                className="gap-2 border-amber-300 text-amber-700 hover:bg-amber-50"
                data-testid="db-import-btn"
              >
                {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {importing ? 'Importing...' : 'Import Backup'}
              </Button>
            </div>
          </div>
        </div>

        {/* Import Confirmation Dialog */}
        <AlertDialog open={showImportConfirm} onOpenChange={setShowImportConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" /> Restore Database from Backup?
              </AlertDialogTitle>
              <AlertDialogDescription>
                This will <strong>replace all existing data</strong> with the contents of <strong>{importFile?.name}</strong>. This action cannot be undone. Make sure you have a current export before proceeding.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => { setImportFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleImportConfirm} className="bg-amber-600 hover:bg-amber-700" data-testid="confirm-import-btn">
                Yes, Restore Database
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bento-card" data-testid="stat-db-name">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#FF6B35]/10 rounded-lg flex items-center justify-center">
                <Database className="h-6 w-6 text-[#FF6B35]" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Database</p>
                <p className="text-lg font-bold text-slate-900 font-mono">{stats?.database_name}</p>
              </div>
            </div>
          </div>
          <div className="bento-card" data-testid="stat-total-docs">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <FileText className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Documents</p>
                <p className="text-2xl font-bold text-slate-900">{stats?.total_documents}</p>
              </div>
            </div>
          </div>
          <div className="bento-card" data-testid="stat-storage">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <HardDrive className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Storage Used</p>
                <p className="text-2xl font-bold text-slate-900">{formatBytes(stats?.storage_size_bytes)}</p>
                <p className="text-xs text-slate-400">{formatBytes(stats?.data_size_bytes)} data</p>
              </div>
            </div>
          </div>
        </div>

        {/* Encryption Status */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-8" data-testid="encryption-section">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <Lock className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Encryption Status</h2>
              <p className="text-sm text-slate-500">Data-at-rest encryption for compliance submissions</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Method</p>
              <p className="font-semibold text-slate-900 flex items-center gap-2">
                <Shield className="h-4 w-4 text-emerald-600" />
                {stats?.encryption?.method}
              </p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Scope</p>
              <p className="font-semibold text-slate-900">{stats?.encryption?.scope}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Encrypted Submissions</p>
              <p className="font-semibold text-slate-900">
                {stats?.encryption?.encrypted_submissions} / {stats?.encryption?.total_submissions}
              </p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Company Keys</p>
              <p className="font-semibold text-slate-900 text-lg">{stats?.encryption?.company_keys_generated}</p>
              <p className="text-xs text-slate-400 mt-1">{stats?.encryption?.key_isolation}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Coverage</p>
              <div className="flex items-center gap-2">
                <p className="font-semibold text-emerald-600 text-lg">{stats?.encryption?.encryption_coverage}</p>
                {stats?.encryption?.encryption_coverage === '100%' && (
                  <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">Full</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Collections */}
        <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="collections-section">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
              <Database className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Collections</h2>
              <p className="text-sm text-slate-500">Document counts per collection</p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {stats?.collections?.map((coll) => {
              const Icon = COLL_ICONS[coll.name] || FileText;
              return (
                <div key={coll.name} className="flex items-center gap-3 bg-slate-50 rounded-lg p-4" data-testid={`coll-${coll.name}`}>
                  <Icon className="h-5 w-5 text-slate-400" />
                  <div>
                    <p className="font-medium text-slate-900 capitalize">{coll.name}</p>
                    <p className="text-sm text-slate-500">{coll.documents} document{coll.documents !== 1 ? 's' : ''}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
